// FiscalDataPage, CPF + address for NF-e issuance

import { useEffect, useState } from 'react';
import { Receipt, Save, CheckCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';

interface FiscalForm {
  cpf: string;
  fiscal_address: string;
  fiscal_number: string;
  fiscal_complement: string;
  fiscal_neighborhood: string;
  fiscal_city: string;
  fiscal_state: string;
  fiscal_zip_code: string;
}

const EMPTY_FORM: FiscalForm = {
  cpf: '', fiscal_address: '', fiscal_number: '', fiscal_complement: '',
  fiscal_neighborhood: '', fiscal_city: '', fiscal_state: '', fiscal_zip_code: '',
};

function formatCPF(value: string) {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0,3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0,3)}.${digits.slice(3,6)}.${digits.slice(6)}`;
  return `${digits.slice(0,3)}.${digits.slice(3,6)}.${digits.slice(6,9)}-${digits.slice(9,11)}`;
}

function formatZip(value: string) {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0,5)}-${digits.slice(5)}`;
}

const BR_STATES = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG',
  'PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO',
];

export function FiscalDataPage() {
  const { profile, refreshProfile } = useAuth();
  const [form, setForm]       = useState<FiscalForm>(EMPTY_FORM);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    if (!profile) return;
    const addr = profile.fiscal_address ?? {};
    setForm({
      cpf:                  profile.cpf ?? '',
      fiscal_address:       addr.street ?? '',
      fiscal_number:        addr.number ?? '',
      fiscal_complement:    addr.complement ?? '',
      fiscal_neighborhood:  addr.neighborhood ?? '',
      fiscal_city:          profile.fiscal_city ?? addr.city ?? '',
      fiscal_state:         profile.fiscal_state ?? addr.state ?? '',
      fiscal_zip_code:      profile.fiscal_zip_code ?? addr.zip_code ?? '',
    });
  }, [profile?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function set(field: keyof FiscalForm, value: string) {
    setForm(prev => ({ ...prev, [field]: value }));
    setSaved(false);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSaved(false);

    const { error: err } = await supabase
      .from('profiles')
      .update({
        cpf:            form.cpf.replace(/\D/g, ''),
        fiscal_address: {
          street:       form.fiscal_address,
          number:       form.fiscal_number,
          complement:   form.fiscal_complement,
          neighborhood: form.fiscal_neighborhood,
          city:         form.fiscal_city,
          state:        form.fiscal_state,
          zip_code:     form.fiscal_zip_code.replace(/\D/g, ''),
        },
        fiscal_city:     form.fiscal_city,
        fiscal_state:    form.fiscal_state,
        fiscal_zip_code: form.fiscal_zip_code.replace(/\D/g, ''),
      })
      .eq('id', profile!.id);

    if (err) {
      setError('Erro ao salvar. Tente novamente.');
    } else {
      setSaved(true);
      if (typeof refreshProfile === 'function') refreshProfile();
    }
    setSaving(false);
  }

  return (
    <div className="page-container" style={{ maxWidth: '600px' }}>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <Receipt size={24} color="var(--petrol)" />
        <div>
          <h1 style={{ fontSize: 'var(--text-title)', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
            Dados Fiscais
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>
            CPF e endereço para emissão de Nota Fiscal
          </p>
        </div>
      </div>

      <div className="card">
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-caption)', marginBottom: '1.5rem', lineHeight: 1.5 }}>
          Esses dados são usados exclusivamente para emissão de Nota Fiscal de Serviço (NFS-e)
          nas suas compras. A NF é enviada automaticamente por e-mail após cada pagamento.
        </p>

        <form onSubmit={handleSave}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

            {/* CPF */}
            <div>
              <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 'var(--text-caption)', marginBottom: '0.35rem' }}>
                CPF
              </label>
              <input
                type="text"
                inputMode="numeric"
                placeholder="000.000.000-00"
                value={form.cpf}
                onChange={e => set('cpf', formatCPF(e.target.value))}
                style={{
                  width: '100%', padding: '0.65rem 0.875rem',
                  backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)',
                  border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-card)',
                  fontSize: 'var(--text-body)', outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>

            {/* ZIP */}
            <div>
              <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 'var(--text-caption)', marginBottom: '0.35rem' }}>
                CEP
              </label>
              <input
                type="text"
                inputMode="numeric"
                placeholder="00000-000"
                value={form.fiscal_zip_code}
                onChange={e => set('fiscal_zip_code', formatZip(e.target.value))}
                style={{
                  width: '100%', padding: '0.65rem 0.875rem',
                  backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)',
                  border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-card)',
                  fontSize: 'var(--text-body)', outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Street + Number */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.75rem' }}>
              <div>
                <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 'var(--text-caption)', marginBottom: '0.35rem' }}>
                  Logradouro
                </label>
                <input
                  type="text"
                  placeholder="Rua, Avenida, etc."
                  value={form.fiscal_address}
                  onChange={e => set('fiscal_address', e.target.value)}
                  style={{
                    width: '100%', padding: '0.65rem 0.875rem',
                    backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)',
                    border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-card)',
                    fontSize: 'var(--text-body)', outline: 'none', boxSizing: 'border-box',
                  }}
                />
              </div>
              <div style={{ width: '90px' }}>
                <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 'var(--text-caption)', marginBottom: '0.35rem' }}>
                  Número
                </label>
                <input
                  type="text"
                  placeholder="100"
                  value={form.fiscal_number}
                  onChange={e => set('fiscal_number', e.target.value)}
                  style={{
                    width: '100%', padding: '0.65rem 0.875rem',
                    backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)',
                    border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-card)',
                    fontSize: 'var(--text-body)', outline: 'none', boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>

            {/* Complement + Neighborhood */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              {[
                { key: 'fiscal_complement' as const, label: 'Complemento', placeholder: 'Apto 12' },
                { key: 'fiscal_neighborhood' as const, label: 'Bairro', placeholder: 'Centro' },
              ].map(({ key, label, placeholder }) => (
                <div key={key}>
                  <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 'var(--text-caption)', marginBottom: '0.35rem' }}>
                    {label}
                  </label>
                  <input
                    type="text"
                    placeholder={placeholder}
                    value={form[key]}
                    onChange={e => set(key, e.target.value)}
                    style={{
                      width: '100%', padding: '0.65rem 0.875rem',
                      backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)',
                      border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-card)',
                      fontSize: 'var(--text-body)', outline: 'none', boxSizing: 'border-box',
                    }}
                  />
                </div>
              ))}
            </div>

            {/* City + State */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.75rem' }}>
              <div>
                <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 'var(--text-caption)', marginBottom: '0.35rem' }}>
                  Cidade
                </label>
                <input
                  type="text"
                  placeholder="São Paulo"
                  value={form.fiscal_city}
                  onChange={e => set('fiscal_city', e.target.value)}
                  style={{
                    width: '100%', padding: '0.65rem 0.875rem',
                    backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)',
                    border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-card)',
                    fontSize: 'var(--text-body)', outline: 'none', boxSizing: 'border-box',
                  }}
                />
              </div>
              <div style={{ width: '80px' }}>
                <label style={{ display: 'block', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 'var(--text-caption)', marginBottom: '0.35rem' }}>
                  Estado
                </label>
                <select
                  value={form.fiscal_state}
                  onChange={e => set('fiscal_state', e.target.value)}
                  style={{
                    width: '100%', padding: '0.65rem 0.5rem',
                    backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)',
                    border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-card)',
                    fontSize: 'var(--text-body)', outline: 'none', boxSizing: 'border-box',
                  }}
                >
                  <option value="">UF</option>
                  {BR_STATES.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                </select>
              </div>
            </div>

          </div>

          {error && (
            <p style={{ color: '#E53E3E', fontSize: 'var(--text-caption)', marginTop: '1rem', marginBottom: 0 }}>{error}</p>
          )}

          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', width: '100%', marginTop: '1.5rem' }}
          >
            {saved ? (
              <><CheckCircle size={16} /> Salvo com sucesso</>
            ) : saving ? 'Salvando...' : (
              <><Save size={16} /> Salvar dados fiscais</>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
